import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import {
  DropDownSelectEvents,
  ENTITIES_CLASS,
  ENTITIES_TYPES,
  ENTITIES_TYPES_ORDER,
  INCIDENT_TYPE,
  RESOURCES_CLASS,
  RESOURCES_TYPES_ORDER,
  ScoreLevels,
} from '../types';
import { html } from 'lit';
import { GzIconLabelWebComponent } from '../../../ui/gzIconLabel';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { IncidentType } from '../../incidentsSideMenu/types';
import { translateFn } from '../../../utils/translator.helper';
import { Feature } from '@nirvana/shared/constants';

export function actorsRenderer(rowData) {
  if (
    rowData.item.specifics.attribution &&
    rowData.item.specifics.attribution.actors &&
    rowData.item.specifics.attribution.actors.length
  ) {
    return html`<span class="cell-label">${renderActorNames(rowData.item.specifics.attribution.actors)}</span>`;
  } else {
    return html`<span class="cell-label">-</span>`;
  }
}

function renderActorNames(actors) {
  return actors.map(actor => actor.name).join(', ');
}

export function entitiesRenderer(rowData) {
  const entities = rowData?.item?.entities;

  if (!entities || !entities.length) {
    return '-';
  }

  const map = {};
  for (const entitiesGroup of entities) {
    const { type, nodes } = entitiesGroup;
    map[type] = nodes;
  }

  const data = new GzTooltipWebComponent();
  data.classList.add('cell-label');
  data.classList.add('list-buttons');
  data.setAttribute('part', 'item-group-icons');
  data.isEllipsis = true;

  const tooltip: Array<string> = [];

  if (rowData['item']['type'] === INCIDENT_TYPE.EDR) {
    //There is only one entity type with only one node
    const endpointName = entities[0]['nodes'][0]['name'];

    tooltip.push(endpointName);
    const gzIcon = new GzIconLabelWebComponent();
    gzIcon.label = endpointName;
    gzIcon.isEllipsis = true;
    gzIcon.uppercase = false;
    gzIcon.icon = ENTITIES_CLASS.get(ENTITIES_TYPES.ENDPOINT);
    gzIcon.setLabelColor('var(--body-text-arrows-400)');
    gzIcon.addEventListener('click', () => this.handleEntityIconClicked(ENTITIES_TYPES.ENDPOINT, rowData.item, gzIcon));
    data.appendChild(gzIcon);
  } else {
    for (const type of ENTITIES_TYPES_ORDER) {
      if (!map[type]) {
        continue;
      }

      const label = map[type].length < 100 ? map[type].length : '99+';

      tooltip.push(this.setEntityStringTranslate(type) + ': ' + map[type].length);
      const gzIcon = new GzIconLabelWebComponent();
      gzIcon.label = label;
      gzIcon.icon = ENTITIES_CLASS.get(type);
      gzIcon.addEventListener('click', () => this.handleEntityIconClicked(type, rowData.item, gzIcon));
      data.appendChild(gzIcon);
    }
  }

  data.tooltipText = tooltip.join(', ');

  return data;
}

export function resourcesRenderer(rowData) {
  const resources = rowData?.item?.resources;
  const tooltip: Array<string> = [];

  if (!resources || !resources.length) {
    return '-';
  }

  const map = {};
  for (const resourcesGroup of resources) {
    const { type, values } = resourcesGroup;
    map[type] = values;
  }

  const data = new GzTooltipWebComponent();
  data.classList.add('cell-label');
  data.classList.add('list-buttons');
  data.setAttribute('part', 'item-group-icons');

  for (const type of RESOURCES_TYPES_ORDER) {
    if (!map[type]) {
      continue;
    }
    const label = map[type].length < 100 ? map[type].length : '99+';

    tooltip.push(this.setResourcesStringTranslate(type) + ': ' + map[type].length);
    const gzIcon = new GzIconLabelWebComponent();
    gzIcon.label = label;
    gzIcon.icon = RESOURCES_CLASS.get(type);

    gzIcon.addEventListener('click', () => this.handleResourceIconClicked(type, rowData.item, gzIcon));
    data.appendChild(gzIcon);
  }

  data.tooltipText = tooltip.join(', ');

  return data;
}

/**
 * Phasr categories(Targeted activity type - column) renderer
 * @param rowData
 */
export function phasrCategoriesRenderer(rowData) {
  const nodesCategories = rowData?.item?.specifics?.nodesCategories;

  if (!nodesCategories || !nodesCategories.length) {
    return '-';
  }

  const tooltipText = nodesCategories
    .map(category => this.stringsTranslator.getPhasrCategoryString(category))
    .join(', ');

  return html` <gz-tooltip class="cell-label" tooltipText="${tooltipText}" part="item-group-icons" isEllipsis
    >${nodesCategories.map((category, index) => {
      return (
        this.stringsTranslator.getPhasrCategoryString(category) + (index !== nodesCategories.length - 1 ? ', ' : '')
      );
    })}
  </gz-tooltip>`;
}

export function scoreRenderer(rowData) {
  const score = rowData?.item?.score;
  const color = getSeverityClassFromScore(score);
  return html`<span class="cell-label has-bullet ${color}">${score}</span>`;
}

export function companyRenderer(rowData) {
  const companyName = rowData?.item?.company.name;
  return html`<span class="cell-label">${companyName}</span>`;
}

export function assigneeRenderer(rowData) {
  const assigneeName = this.dependencies.Ext.getAssigneeName(rowData?.item.assignee);
  return html`<span class="cell-label">${assigneeName}</span>`;
}

export function lastKillChainPhaseRenderer(rowData) {
  const lastKillChainPhaseName = rowData.item?.specifics.lastKillChainPhase
    ? this.stringsTranslator.getKillChainPhasesTranslations(rowData.item?.specifics.lastKillChainPhase)
    : '-';
  return html`<span class="cell-label">${lastKillChainPhaseName}</span>`;
}

export function selectColumnRenderer(rowData) {
  let options = [
    {
      title: this.dependencies.translateFn('View events and alerts'),
      value: DropDownSelectEvents.viewEvents,
      id: rowData.item.id,
      disabled: false
    },
    {
      title: this.dependencies.translateFn('Prioritize'),
      value: DropDownSelectEvents.prioritize,
      id: rowData.item.id,
      disabled: rowData.item.isGzDemo,
      tooltipText: rowData.item.isGzDemo ? this.dependencies.translateFn('Not available in demo mode.') : '',
    },
    {
      title: this.dependencies.translateFn('Assign'),
      value: DropDownSelectEvents.assign,
      id: rowData.item.id,
      disabled: rowData.item.isGzDemo,
      tooltipText: rowData.item.isGzDemo ? this.dependencies.translateFn('Not available in demo mode.') : '',
    },
  ];

  if (!Application.Main.product.isCloud() ||
    !Application.Main.hasFeatures(Feature.PRODUCT_FEATURE_EDR_INCIDENTS) ||
    !Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_HISTORICAL_SEARCH])) {
    options = options.filter(option => option.value !== DropDownSelectEvents.viewEvents);
  }

  const gzSelect = new GzSelectWebComponent();
  const cellContainer = document.createElement('div');
  cellContainer.className = 'd-flex justify-end w-full';

  gzSelect.options = options;
  gzSelect.innerHTML = ` <gz-icon-button
          icon="ds-icon-more"
          fontSize="24"
          buttonType="square"
          buttonFill="quiet"
          buttonColor="secondary">
     </gz-icon-button>`;

  gzSelect.addEventListener(SelectEvents.SELECTION_CHANGED, (e: CustomEvent) => {
    const selectedOption = e.detail.selected;
    const event = new CustomEvent(selectedOption, {
      detail: {
        incident: rowData.item,
      },
      bubbles: true,
      composed: true,
    });

    this.dom.$grid.dispatchEvent(event);
  });
  cellContainer.appendChild(gzSelect);

  return cellContainer;
}

export function getSeverityClassFromScore(score) {
  let color = '';
  switch (true) {
    case score >= ScoreLevels.HIGH:
      color = 'bullet-danger';
      break;
    case score >= ScoreLevels.MEDIUM && score < ScoreLevels.HIGH:
      color = 'bullet-orange';
      break;
    case score >= ScoreLevels.LOW && score < ScoreLevels.MEDIUM:
      color = 'bullet-yellow';
      break;
    default:
      color = 'bullet-neutral';
      break;
  }
  return color;
}

export function createdRenderer(rowData) {
  return html`<span class="cell-label"> ${this.setTimezone(rowData?.item?.created)} </span>`;
}

export function lastUpdatedRenderer(rowData) {
  return html`<span class="cell-label"> ${this.setTimezone(rowData?.item?.lastUpdated)} </span>`;
}

export function idRenderer(rowData) {
  const incidentLink = generateIncidentLink(rowData.item._id, rowData.item.type);
  let linkMessage = `#${rowData.item?.number}`;

  if (rowData.item?.isGzDemo && rowData.item?.type === INCIDENT_TYPE.XDR) {
    linkMessage = `#DEMO`;
  }

  const defaultSizing = 35;
  const defaultSizingGrowth = 7;
  const linkPadding = defaultSizing + linkMessage.length * defaultSizingGrowth + 'px';

  return html`
    <gz-link type="quiet" color="primary" @click=${() => this.handleNumberClick(rowData.item)}>
      ${linkMessage}
    </gz-link>
    <gz-copy-to-clipboard
      style="left: ${linkPadding};
      position: absolute;
      width: auto;
      height: auto;
      right: 0;"
      .confirmationMessage="${translateFn('Copied')}"
      .clipboardMessage="${incidentLink}"
      showIconOnHover="true"
    >
    </gz-copy-to-clipboard>
  `;
}

export function correlatedRenderer(rowData) {
  const correlatedIncidents = rowData.item?.correlatedIncidents;

  if (!correlatedIncidents || correlatedIncidents.length === 0) {
    return '-';
  }
  const tooltipText = correlatedIncidents
    .map(incident =>
      rowData.item?.isGzDemo && incident.type === INCIDENT_TYPE.EDR
        ? '#DEMO-' + incident.demoIncidentOrder
        : '#' + incident.number
    )
    .join(', ');

  return html` <gz-tooltip class="cell-label" tooltipText="${tooltipText}" part="item-group-icons" isEllipsis
    >${correlatedIncidents.map((incident, index) => {
      let linkMessage = `#${incident.number}`;

      if (rowData.item?.isGzDemo && incident.type === INCIDENT_TYPE.EDR) {
        linkMessage = `#DEMO-${incident.demoIncidentOrder}`;
      }

      return html`
        <gz-link type="quiet" color="primary" @click=${() => this.handleCorrelatedClick(incident)}>
          ${linkMessage} </gz-link
        >${index !== correlatedIncidents.length - 1 ? ', ' : ''}
      `;
    })}
  </gz-tooltip>`;
}

export function generateIncidentLink(incidentId: string, incidentType: IncidentType): string {
  if (incidentType === IncidentType.EDR) {
    return window.location.origin + '/#!/incidents/' + incidentId;
  }
  return window.location.origin + '/#!/incidents/extended/' + incidentId;
}
